import { useState, useCallback, useRef, useLayoutEffect, useEffect } from 'react';

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 1.5;
const ZOOM_STEP = 0.1;
// Fit's own legible floor (round 5, ux-fixes-brief.md Task 2, MAJOR #5):
// higher than ZOOM_MIN - a manual Zoom out can still go all the way down to
// 40%, but Fit itself should never land somewhere unreadable on its own.
const FIT_MIN = 0.5;

const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
// Snap to the nearest 0.1 so repeated +/- 0.1 steps never drift off-grid on
// float error (0.1 + 0.1 + 0.1 !== 0.3 in IEEE 754).
const snapZoom = (z) => Math.round(z * 10) / 10;

/**
 * useOrgChartZoom - zoom state and the DOM plumbing behind the org chart's
 * zoom controls: measuring the tree's true unscaled size, sizing the
 * scrollable box to match, and wiring a non-passive wheel listener.
 *
 * `transform: scale(zoom)` on the wrapper only affects paint, not layout, so
 * the wrapper's own scrollWidth/scrollHeight always report the tree's true,
 * unscaled size - re-measured whenever `collapsedIds`/`nodes` change (the
 * visible tree shape) and on any resize. The sizing box around it is set to
 * that size times zoom, so the canvas's scrollable area matches what is
 * actually painted (no dead whitespace at zoom < 1, full reach at zoom > 1).
 *
 * @param {{ collapsedIds: Set, nodes: Array }} deps - values whose identity
 *   changes exactly when the rendered tree's shape (and so its true size)
 *   might have changed. Passed as an explicit object (not a caller-built
 *   array) so the internal effect's dependency list stays a literal array
 *   `react-hooks/exhaustive-deps` can actually check.
 */
const useOrgChartZoom = ({ collapsedIds, nodes }) => {
  const [zoom, setZoom] = useState(1);
  const [unscaledSize, setUnscaledSize] = useState({ width: 0, height: 0 });
  // The canvas's own visible size (its `clientWidth`/`clientHeight`, not the
  // scrollable content inside it) - read directly by the initial-auto-fit
  // decision below (is the tree wider than the canvas?). Used to publish a
  // `--org-canvas-h` CSS variable in earlier rounds (the orphans panel's own
  // `max-height` capped against it); that consumer is gone as of round 5's
  // drawer rework (`OrgOrphansPanel.js` sizes itself directly against the
  // shell instead), so this state is JS-only now - fix round 1 dropped the
  // now-dead `canvasStyle` CSS-variable emission rather than leave an
  // always-present-but-inert custom property on the canvas element.
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const canvasRef = useRef(null);
  const treeWrapperRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const canvasResizeObserverRef = useRef(null);

  const measureTree = useCallback(() => {
    const el = treeWrapperRef.current;
    if (!el) return;
    const width = el.scrollWidth;
    const height = el.scrollHeight;
    // Bail out on an unchanged measurement - a ResizeObserver still fires on
    // every layout pass even when nothing actually moved, and setting a new
    // object identity here would re-render the whole tree, which can nudge
    // layout again and re-trigger the observer: a measure -> render -> resize
    // -> measure loop.
    setUnscaledSize((prev) => (
      prev.width === width && prev.height === height ? prev : { width, height }
    ));
  }, []);

  useLayoutEffect(() => {
    measureTree();
  }, [measureTree, collapsedIds, nodes]);

  // Attaches/detaches the ResizeObserver from a callback ref, the same
  // pattern as `setCanvasRef` below - NOT a `useLayoutEffect` keyed on
  // `[measureTree]`. `measureTree` is a stable identity (empty dep array),
  // so an effect keyed on it only ever runs once, at first commit; on first
  // commit the page is usually still in its loading state and this wrapper
  // has not rendered yet, so `treeWrapperRef.current` is null and that one
  // run is a no-op forever after - the observer never actually attaches in
  // the real app. A callback ref instead fires exactly when React mounts or
  // unmounts the wrapper element, whenever that happens to be.
  const setTreeWrapperRef = useCallback((el) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    treeWrapperRef.current = el;
    if (el && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measureTree);
      observer.observe(el);
      resizeObserverRef.current = observer;
    }
    // The tree shape may already differ from the last measurement by the
    // time this element mounts (e.g. a fast mode switch) - measure right
    // away rather than waiting for the next collapsedIds/nodes change.
    measureTree();
  }, [measureTree]);

  const zoomIn = useCallback(() => {
    setZoom((z) => snapZoom(clampZoom(z + ZOOM_STEP)));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => snapZoom(clampZoom(z - ZOOM_STEP)));
  }, []);

  // Exposed so the page can put zoom back to 100% on a fresh fetch (e.g.
  // Retry after an error) - the old behaviour before zoom moved into this
  // hook, restored here rather than left as a silent regression.
  const resetZoom = useCallback(() => setZoom(1), []);

  // Reads the measured `unscaledSize` state - the same single source of
  // truth the sizing box's own style is built from - rather than taking a
  // fresh DOM read, so Fit's math and the sizing box's math can never
  // disagree with each other.
  //
  // Round 5 (ux-fixes-brief.md Task 2, MAJOR #5): WIDTH only, not
  // `min(widthRatio, heightRatio)` - the old two-axis version chose whichever
  // ratio was smaller, which on a wide-but-shallow company tree could pick a
  // needlessly tiny zoom just to satisfy an axis nobody was worried about;
  // the canvas already scrolls vertically, so there is no correctness need
  // to guarantee the WHOLE tree's height fits on screen at once, only that
  // it reads clearly left-to-right. `FIT_MIN` (0.5) floors the result so Fit
  // itself never lands somewhere unreadable (a manual Zoom out can still go
  // lower, to `ZOOM_MIN`) - on a very wide tree this means Fit may leave some
  // horizontal overflow rather than shrink text past legibility, which is
  // the correct trade-off here. After setting zoom, scrolling to (0, 0) is
  // what makes the root visible top-left rather than wherever the browser's
  // default scroll anchoring left it; `.org-chart-canvas`'s own
  // `justify-content: safe center` (orgchart.css) then centres the result
  // horizontally for free whenever the fitted tree ends up narrower than the
  // canvas - `safe` degrades to left-alignment (keeping x=0 reachable)
  // exactly when it doesn't, so this scroll-reset is never fighting it.
  const fit = useCallback(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl || !unscaledSize.width || !unscaledSize.height) return;
    const widthRatio = canvasEl.clientWidth / unscaledSize.width;
    const stepped = Math.floor(clampZoom(widthRatio) * 10 + 1e-9) / 10;
    setZoom(Math.max(FIT_MIN, clampZoom(stepped)));
    canvasEl.scrollTop = 0;
    canvasEl.scrollLeft = 0;
  }, [unscaledSize.width, unscaledSize.height]);

  // Ctrl/Cmd + wheel zooms; a plain wheel keeps scrolling. Trackpad pinch
  // arrives as ctrl+wheel in Chromium/Safari, so this covers pinch too.
  // A native listener (not React's onWheel) is required: React registers
  // wheel handlers as passive by default, so `preventDefault` inside a JSX
  // onWheel would be silently ignored (and warn) instead of stopping the
  // browser's own page-zoom gesture.
  const handleWheelZoom = useCallback((event) => {
    if (!(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    if (event.deltaY < 0) zoomIn();
    else if (event.deltaY > 0) zoomOut();
  }, [zoomIn, zoomOut]);

  // Reads the canvas's own visible size (not its scrollable content) -
  // independent of `measureTree` above, which reads the tree wrapper's
  // content size instead. Same no-op-on-unchanged-size bail-out as
  // `measureTree`, for the same reason (a ResizeObserver fires on every
  // layout pass, not just ones that actually changed anything).
  const measureCanvas = useCallback(() => {
    const el = canvasRef.current;
    if (!el) return;
    const width = el.clientWidth;
    const height = el.clientHeight;
    setCanvasSize((prev) => (
      prev.width === width && prev.height === height ? prev : { width, height }
    ));
  }, []);

  // Same callback-ref pattern as `setTreeWrapperRef` above (see its comment
  // for why a callback ref, not a `useLayoutEffect`, is required), extended
  // to also attach/detach a second, independent ResizeObserver for the
  // canvas's own size - kept separate from the tree wrapper's observer since
  // they watch different elements for different reasons.
  const setCanvasRef = useCallback((el) => {
    if (canvasRef.current && canvasRef.current !== el) {
      canvasRef.current.removeEventListener('wheel', handleWheelZoom);
    }
    if (canvasResizeObserverRef.current) {
      canvasResizeObserverRef.current.disconnect();
      canvasResizeObserverRef.current = null;
    }
    canvasRef.current = el;
    if (el) {
      el.addEventListener('wheel', handleWheelZoom, { passive: false });
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(measureCanvas);
        observer.observe(el);
        canvasResizeObserverRef.current = observer;
      }
    }
    measureCanvas();
  }, [handleWheelZoom, measureCanvas]);

  // Initial auto-fit (round 5, ux-fixes-brief.md Task 2, BLOCKER #1 /
  // MINOR #8): runs once per fresh dataset (keyed on `nodes`' own identity,
  // via `autoFitHandledForRef` below - NOT on every `collapsedIds` change,
  // so a manual expand/collapse never re-triggers it), once BOTH the tree's
  // true size and the canvas's own visible size have actually been
  // measured (both start at `{0, 0}`, so a `0` reads as "not measured yet",
  // not "an empty tree" - a real empty tree never reaches this hook's
  // canvas branch at all, see OrgChart.js's `nodes.length === 0` guard).
  //
  // If the unscaled tree is wider than the canvas, this is exactly `fit`'s
  // own job (width-fit + scroll to top-left) - reusing it here keeps the
  // "how do we fit a wide tree" decision in exactly one place. Otherwise
  // the tree already fits at 100% (which `resetZoom`, called by the page on
  // every fresh fetch, already put `zoom` at) - the only thing left to do
  // is make sure the canvas is scrolled to its own origin, so the root sits
  // near the top-left with normal breathing room instead of wherever the
  // browser's own scroll-restoration/anchoring happened to leave it, e.g.
  // on a Retry after scrolling around the previous (now-replaced) tree.
  const autoFitHandledForRef = useRef(null);
  useEffect(() => {
    if (!unscaledSize.width || !unscaledSize.height) return;
    if (!canvasSize.width || !canvasSize.height) return;
    if (autoFitHandledForRef.current === nodes) return;
    autoFitHandledForRef.current = nodes;

    if (unscaledSize.width > canvasSize.width) {
      fit();
    } else {
      const canvasEl = canvasRef.current;
      if (canvasEl) {
        canvasEl.scrollTop = 0;
        canvasEl.scrollLeft = 0;
      }
    }
  }, [nodes, unscaledSize.width, unscaledSize.height, canvasSize.width, canvasSize.height, fit]);

  const sizeboxStyle = {
    width: unscaledSize.width * zoom,
    height: unscaledSize.height * zoom,
  };
  const wrapperStyle = { transform: `scale(${zoom})`, transformOrigin: 'top left' };

  return {
    zoom,
    setCanvasRef,
    treeWrapperRef,
    setTreeWrapperRef,
    sizeboxStyle,
    wrapperStyle,
    zoomIn,
    zoomOut,
    fit,
    resetZoom,
  };
};

export default useOrgChartZoom;
